import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import Breadcrumb from "../../../../core/common/Breadcrumb/breadcrumb";
import Sidebar from "../../../../core/common/sidebar/sidebar";
import API_URLS from "../../../../core/common/apiUrls";

type FlightBooking = {
  booking_id?: number | string;
  pnr?: string;
  tbo_booking_id?: string;
  status?: string;
  amount?: number;
  currency?: string;
  created_at?: string;
  is_hold?: boolean;
  hold_amount?: number | null;
  hold_until?: string | null;
  razorpay_payment_id?: string;
  airline?: { name?: string; iata_code?: string };
  segments?: any[];
  passengers?: any[];
  ticket?: { status?: string; pdf_path?: string; issued_at?: string } | null;
  invoice?: { invoice_number?: string; pdf_path?: string } | null;
  [k: string]: any;
};

type Tab = "upcoming" | "held" | "past" | "cancelled";

const getFromTo = (booking: FlightBooking) => {
  try {
    const segs: any[][] = booking.segments || [];
    const first = segs[0]?.[0];
    const last = segs[segs.length - 1]?.[segs[segs.length - 1].length - 1];
    return {
      from: first?.Origin?.Airport?.CityName || first?.Origin?.Airport?.AirportCode || "—",
      to: last?.Destination?.Airport?.CityName || last?.Destination?.Airport?.AirportCode || "—",
      depart: first?.Origin?.DepTime ? new Date(first.Origin.DepTime) : null,
    };
  } catch {
    return { from: "—", to: "—", depart: null };
  }
};

const formatDate = (d: Date | null) =>
  d ? d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const parseHoldDate = (raw: string | null | undefined): Date | null => {
  if (!raw) return null;
  const legacy = raw.match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2})/);
  if (legacy) {
    const [, dd, mm, yyyy, hh, min] = legacy;
    return new Date(`${yyyy}-${mm}-${dd}T${hh}:${min}:00`);
  }
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

const classifyBooking = (f: FlightBooking): Tab => {
  const status = (f.status || "").toLowerCase();
  if (status === "held") return "held";
  if (["cancelled", "hold_expired", "canceled"].includes(status)) return "cancelled";
  const { depart } = getFromTo(f);
  if (depart && depart > new Date()) return "upcoming";
  return "past";
};

const CANCELLABLE_STATUSES = ["confirmed", "upcoming", "booked", "ticketed"];

const UserFlightBooking: React.FC = () => {
  const routes = all_routes;
  const navigate = useNavigate();

  const userId = useMemo(() => {
    const sid = sessionStorage.getItem("user_id");
    if (sid) return Number(sid);
    try {
      const u = JSON.parse(sessionStorage.getItem("user") || "null");
      return u?.id ?? null;
    } catch {
      return null;
    }
  }, []);

  const [list, setList] = useState<FlightBooking[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("upcoming");
  const [cancelModal, setCancelModal] = useState<{
    booking: FlightBooking;
    charges: any | null;
    chargesLoading: boolean;
    chargesError: string | null;
    cancelling: boolean;
    cancelError: string | null;
  } | null>(null);

  const fetchBookings = async () => {
    if (!userId) { setErr("User not found in session. Please sign in again."); setList([]); return; }
    setLoading(true); setErr(null);
    try {
      const res = await fetch(API_URLS.FLIGHTS.MY_BOOKINGS, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      const data = await res.json();
      const rows: FlightBooking[] = Array.isArray(data) ? data : (data.bookings || data.data || []);
      setList(rows || []);
    } catch (e: any) {
      setErr(e?.message || "Failed to load bookings.");
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBookings(); }, [userId]); // eslint-disable-line

  const openCancelModal = async (booking: FlightBooking) => {
    setCancelModal({ booking, charges: null, chargesLoading: true, chargesError: null, cancelling: false, cancelError: null });
    if (booking.tbo_booking_id && !booking.is_hold) {
      try {
        const res = await fetch(API_URLS.FLIGHTS.CANCELLATION, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "GetCancellationCharges", BookingId: booking.tbo_booking_id, RequestType: 1, BookingMode: 5 }),
        });
        const data = await res.json();
        setCancelModal(prev => prev ? { ...prev, chargesLoading: false, charges: data } : null);
      } catch (e: any) {
        setCancelModal(prev => prev ? { ...prev, chargesLoading: false, chargesError: "Could not fetch cancellation charges. You can still proceed." } : null);
      }
    } else {
      setCancelModal(prev => prev ? { ...prev, chargesLoading: false } : null);
    }
  };

  const handleConfirmCancel = async () => {
    if (!cancelModal) return;
    const { booking } = cancelModal;
    setCancelModal(prev => prev ? { ...prev, cancelling: true, cancelError: null } : null);
    try {
      if (booking.tbo_booking_id) {
        const action = booking.is_hold ? "ReleasePNR" : "AutoCancel";
        const payload: any = booking.is_hold
          ? { action, BookingId: booking.tbo_booking_id, Source: 1 }
          : { action, BookingId: booking.tbo_booking_id, RequestType: 1, CancellationType: 1, Remarks: "Customer requested cancellation" };
        const res = await fetch(API_URLS.FLIGHTS.CANCELLATION, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d.error || `Cancellation failed (${res.status})`);
        }
      }
      if (booking.razorpay_payment_id) {
        let refundAmount: number;
        if (booking.is_hold) {
          refundAmount = booking.hold_amount ?? 0;
        } else {
          const chargeAmount = cancelModal.charges?.Response?.CancellationCharges?.[0]?.TotalCharge ?? 0;
          refundAmount = Math.max(0, (booking.amount ?? 0) - chargeAmount);
        }
        if (refundAmount > 0) {
          await fetch(API_URLS.PAYMENTS.REFUND_FLIGHT, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ booking_id: booking.booking_id, razorpay_payment_id: booking.razorpay_payment_id, amount: refundAmount }),
          }).catch(() => {});
        }
      }
      setCancelModal(null);
      await fetchBookings();
    } catch (e: any) {
      setCancelModal(prev => prev ? { ...prev, cancelling: false, cancelError: e?.message || "Cancellation failed. Please try again." } : null);
    }
  };

  const grouped = useMemo(() => {
    const g: Record<Tab, FlightBooking[]> = { upcoming: [], held: [], past: [], cancelled: [] };
    (list || []).forEach(f => g[classifyBooking(f)].push(f));
    return g;
  }, [list]);

  const tabConfig: { key: Tab; label: string; color: string }[] = [
    { key: "upcoming", label: "Upcoming", color: "primary" },
    { key: "held",     label: "Locked Price", color: "warning" },
    { key: "past",     label: "Past",     color: "secondary" },
    { key: "cancelled",label: "Cancelled",color: "danger" },
  ];

  const breadcrumbs = [
    { label: "Home", link: routes.home1, active: false },
    { label: "My Bookings", active: true },
    { label: "Flights", active: true },
  ];

  const currentList = grouped[activeTab];

  return (
    <div>
      <Breadcrumb title="My Bookings" breadcrumbs={breadcrumbs} backgroundClass="breadcrumb-bg-04" />
      <div className="content">
        <div className="container">
          <div className="row">
            <div className="col-xl-3 col-lg-4">
              <Sidebar />
            </div>

            <div className="col-xl-9 col-lg-8 theiaStickySidebar">
              {/* Header */}
              <div className="card booking-header mb-3">
                <div className="card-body header-content d-flex align-items-center justify-content-between flex-wrap">
                  <div>
                    <h6 className="mb-0">Flight Bookings</h6>
                    <p className="fs-14 text-gray-6 fw-normal mb-0">{(list?.length || 0)} total bookings</p>
                  </div>
                  <button className="btn btn-light btn-sm" onClick={fetchBookings} disabled={loading}>
                    <i className={`isax isax-refresh me-1 ${loading ? "fa-spin" : ""}`} /> Refresh
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="d-flex gap-2 mb-3 flex-wrap">
                {tabConfig.map(t => (
                  <button
                    key={t.key}
                    type="button"
                    className={`btn btn-sm rounded-pill px-3 ${activeTab === t.key ? `btn-${t.color}` : `btn-outline-${t.color}`}`}
                    onClick={() => setActiveTab(t.key)}
                  >
                    {t.label}
                    {grouped[t.key].length > 0 && (
                      <span className={`badge ms-2 ${activeTab === t.key ? "bg-white text-dark" : `bg-${t.color} text-white`}`}>
                        {grouped[t.key].length}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Content */}
              <div className="card hotel-list">
                <div className="card-body p-0">
                  {loading ? (
                    <div className="p-5 text-center">
                      <div className="spinner-border text-primary" />
                      <p className="mt-2 text-muted">Loading bookings…</p>
                    </div>
                  ) : err ? (
                    <div className="p-4 text-danger">{err}</div>
                  ) : currentList.length === 0 ? (
                    <div className="p-5 text-center text-muted">
                      <i className="isax isax-airplane fs-40 d-block mb-2" />
                      No {activeTab === "held" ? "locked price" : activeTab} bookings found.
                    </div>
                  ) : (
                    <div className="p-3 d-flex flex-column gap-3">
                      {currentList.map((f, idx) => {
                        const { from, to, depart } = getFromTo(f);
                        const remaining = (f.amount != null && f.hold_amount != null)
                          ? f.amount - f.hold_amount : f.amount;
                        const isCancellable = CANCELLABLE_STATUSES.includes((f.status || "").toLowerCase());
                        const isHoldExpired = (() => {
                          const d = parseHoldDate(f.hold_until);
                          return d ? d < new Date() : false;
                        })();

                        return (
                          <div key={String(f.pnr || f.booking_id || idx)} className="border rounded-3 overflow-hidden">
                            {/* Card header */}
                            <div className={`px-3 py-2 d-flex align-items-center justify-content-between flex-wrap gap-2 ${
                              activeTab === "held" ? "bg-warning bg-opacity-10 border-bottom border-warning" :
                              activeTab === "upcoming" ? "bg-primary bg-opacity-10 border-bottom border-primary" :
                              activeTab === "cancelled" ? "bg-danger bg-opacity-10 border-bottom border-danger" :
                              "bg-light border-bottom"
                            }`}>
                              <div className="d-flex align-items-center gap-3">
                                <div>
                                  <span className="fw-bold fs-14">{f.airline?.name || "Flight"}</span>
                                  <span className="text-muted fs-12 ms-2">{f.airline?.iata_code}</span>
                                </div>
                                <div className="d-flex align-items-center gap-2 fs-14">
                                  <span className="fw-semibold">{from}</span>
                                  <i className="isax isax-airplane fs-12 text-muted" />
                                  <span className="fw-semibold">{to}</span>
                                </div>
                                <span className="text-muted fs-12">{formatDate(depart)}</span>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <span className="badge bg-light text-dark border fs-11">PNR: {f.pnr || "—"}</span>
                                {activeTab === "held" && (
                                  <span className="badge bg-warning text-dark">🔒 Locked Price</span>
                                )}
                                {activeTab === "upcoming" && (
                                  <span className="badge bg-primary">Upcoming</span>
                                )}
                                {activeTab === "past" && (
                                  <span className="badge bg-secondary">Completed</span>
                                )}
                                {activeTab === "cancelled" && (
                                  <span className="badge bg-danger">{f.status === "hold_expired" ? "Lock Expired" : "Cancelled"}</span>
                                )}
                              </div>
                            </div>

                            {/* Card body */}
                            <div className="px-3 py-3">
                              <div className="row gy-2 align-items-center">
                                <div className="col-sm-6">
                                  <div className="d-flex gap-4 text-muted fs-13">
                                    <div>
                                      <div className="fw-semibold text-dark fs-14">
                                        {f.currency || "INR"} {f.amount != null ? Number(f.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 }) : "—"}
                                      </div>
                                      <div className="fs-11">Total Fare</div>
                                    </div>
                                    {activeTab === "held" && f.hold_amount != null && (
                                      <>
                                        <div>
                                          <div className="fw-semibold text-success fs-14">₹{Number(f.hold_amount).toLocaleString("en-IN")}</div>
                                          <div className="fs-11">Deposit Paid</div>
                                        </div>
                                        <div>
                                          <div className="fw-semibold text-danger fs-14">₹{Number(remaining).toLocaleString("en-IN")}</div>
                                          <div className="fs-11">Remaining</div>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                  {activeTab === "held" && f.hold_until && (
                                    <div className={`mt-1 fs-12 fw-medium ${isHoldExpired ? "text-danger" : "text-warning"}`}>
                                      <i className={`isax ${isHoldExpired ? "isax-warning-2" : "isax-clock"} me-1`} />
                                      {isHoldExpired
                                        ? `Expired: ${f.hold_until}`
                                        : `Deadline: ${f.hold_until}`}
                                    </div>
                                  )}
                                  {activeTab === "held" && !f.hold_until && (
                                    <div className="mt-1 fs-12 text-muted">No hold deadline set by airline</div>
                                  )}
                                </div>

                                <div className="col-sm-6 d-flex flex-wrap gap-2 justify-content-sm-end">
                                  {/* HELD: Complete booking (disabled if expired) */}
                                  {activeTab === "held" && !isHoldExpired && (
                                    <button
                                      type="button"
                                      className="btn btn-warning btn-sm fw-semibold"
                                      onClick={() => navigate(routes.flightHoldConfirmation, {
                                        state: {
                                          holdData: {
                                            held_bookings: [{
                                              booking_id: f.booking_id,
                                              tbo_booking_id: f.tbo_booking_id,
                                              pnr: f.pnr,
                                              flight_offer_id: f.flight_offer_id,
                                              result_index: f.result_index,
                                              final_price: f.amount,
                                              currency: f.currency,
                                              hold_until: f.hold_until ?? null,
                                              hold_amount_paid: f.hold_amount ?? 0,
                                              remaining_amount: remaining,
                                              segments: f.segments ?? [],
                                              airline: f.airline,
                                            }],
                                            lead_passenger: (() => {
                                      const p = f.passengers?.find((px: any) => px.is_lead) ?? f.passengers?.[0] ?? {};
                                      return {
                                        first_name: p.first_name || "",
                                        last_name: p.last_name || "",
                                        email: p.email || "",
                                        phone: p.phone || p.contact_no || "",
                                      };
                                    })(),
                                          },
                                          totalFare: f.amount,
                                          holdDepositAmount: f.hold_amount ?? 0,
                                        },
                                      })}
                                    >
                                      <i className="isax isax-wallet me-1" />
                                      Complete Booking
                                    </button>
                                  )}
                                  {activeTab === "held" && isHoldExpired && (
                                    <div className="text-center">
                                      <span className="badge bg-danger fs-12 px-3 py-2 d-block mb-1">
                                        Hold Expired
                                      </span>
                                      <span className="text-muted fs-11">
                                        Price lock has lapsed. Your deposit will be refunded within 5–7 business days.
                                      </span>
                                    </div>
                                  )}

                                  {/* UPCOMING: Download ticket + Cancel */}
                                  {activeTab === "upcoming" && (
                                    <>
                                      {f.pnr && (
                                        <a
                                          href={API_URLS.FLIGHTS.DOWNLOAD_TICKET(f.pnr)}
                                          className="btn btn-outline-primary btn-sm"
                                          download={`ticket_${f.pnr}.pdf`}
                                          target="_blank"
                                          rel="noreferrer"
                                        >
                                          <i className="isax isax-document-download me-1" />
                                          Ticket
                                        </a>
                                      )}
                                      {isCancellable && (
                                        <button type="button" className="btn btn-outline-danger btn-sm"
                                          onClick={() => openCancelModal(f)}>
                                          <i className="isax isax-close-circle me-1" />
                                          Cancel Ticket
                                        </button>
                                      )}
                                    </>
                                  )}

                                  {/* PAST: Download ticket + invoice */}
                                  {activeTab === "past" && f.pnr && (
                                    <>
                                      <a
                                        href={API_URLS.FLIGHTS.DOWNLOAD_TICKET(f.pnr)}
                                        className="btn btn-outline-secondary btn-sm"
                                        download={`ticket_${f.pnr}.pdf`}
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        <i className="isax isax-document-download me-1" />
                                        Ticket
                                      </a>
                                      <a
                                        href={API_URLS.FLIGHTS.DOWNLOAD_INVOICE(f.pnr)}
                                        className="btn btn-outline-secondary btn-sm"
                                        download={`invoice_${f.pnr}.pdf`}
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        <i className="isax isax-receipt me-1" />
                                        Invoice
                                      </a>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Cancel Modal ── */}
      {cancelModal && (() => {
        const { booking, charges, chargesLoading, chargesError, cancelling, cancelError } = cancelModal;
        const { from, to } = getFromTo(booking);
        const isHold = booking.is_hold;
        const chargeAmount = charges?.Response?.CancellationCharges?.[0]?.TotalCharge ?? null;
        const refundAmount = booking.is_hold
          ? (booking.hold_amount ?? 0)
          : chargeAmount != null
            ? Math.max(0, (booking.amount ?? 0) - chargeAmount)
            : null;

        return (
          <div
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
            onClick={(e) => { if (e.target === e.currentTarget && !cancelling) setCancelModal(null); }}
          >
            <div className="card border-0 shadow-lg" style={{ maxWidth: 480, width: "100%" }}>
              <div className="card-header bg-danger text-white d-flex align-items-center justify-content-between py-3">
                <h6 className="mb-0 fw-semibold">
                  {isHold ? "Cancel Locked Price" : "Cancel Ticket"}
                </h6>
                {!cancelling && (
                  <button type="button" className="btn-close btn-close-white" onClick={() => setCancelModal(null)} />
                )}
              </div>

              <div className="card-body">
                {/* Flight info */}
                <div className="d-flex align-items-center gap-2 mb-3 p-2 bg-light rounded">
                  <span className="fw-semibold">{from}</span>
                  <i className="isax isax-airplane text-muted" />
                  <span className="fw-semibold">{to}</span>
                  <span className="ms-auto badge bg-light text-dark border">PNR: {booking.pnr || "—"}</span>
                </div>

                {isHold ? (
                  <div className="alert alert-warning fs-13 mb-3">
                    <strong>Cancel locked price?</strong> Your deposit of{" "}
                    <strong>₹{Number(booking.hold_amount ?? 0).toLocaleString("en-IN")}</strong> will be refunded.
                    The seat hold with the airline will be released.
                  </div>
                ) : chargesLoading ? (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm text-primary me-2" />
                    <span className="text-muted fs-13">Fetching cancellation charges…</span>
                  </div>
                ) : (
                  <>
                    {chargesError && (
                      <div className="alert alert-warning fs-13 mb-2">{chargesError}</div>
                    )}
                    <ul className="list-unstyled mb-3">
                      <li className="d-flex justify-content-between py-2 border-bottom">
                        <span className="text-muted fs-13">Ticket Amount</span>
                        <span className="fw-semibold">₹{Number(booking.amount ?? 0).toLocaleString("en-IN")}</span>
                      </li>
                      {chargeAmount != null && (
                        <li className="d-flex justify-content-between py-2 border-bottom">
                          <span className="text-muted fs-13">Cancellation Charge</span>
                          <span className="fw-semibold text-danger">− ₹{Number(chargeAmount).toLocaleString("en-IN")}</span>
                        </li>
                      )}
                      {refundAmount != null && (
                        <li className="d-flex justify-content-between py-2">
                          <span className="fw-semibold fs-14">Estimated Refund</span>
                          <span className="fw-bold text-success fs-15">₹{Number(refundAmount).toLocaleString("en-IN")}</span>
                        </li>
                      )}
                    </ul>
                    <p className="text-muted fs-12 mb-3">
                      Refund will be credited to your original payment method within 5–7 business days.
                    </p>
                  </>
                )}

                {cancelError && (
                  <div className="alert alert-danger fs-13 mb-2">{cancelError}</div>
                )}
              </div>

              <div className="card-footer bg-white d-flex gap-2 justify-content-end">
                <button type="button" className="btn btn-outline-secondary" disabled={cancelling}
                  onClick={() => setCancelModal(null)}>
                  Keep Booking
                </button>
                <button type="button" className="btn btn-danger" disabled={cancelling || chargesLoading}
                  onClick={handleConfirmCancel}>
                  {cancelling ? (
                    <><span className="spinner-border spinner-border-sm me-2" />Cancelling…</>
                  ) : isHold ? "Cancel Hold & Refund" : "Confirm Cancellation"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default UserFlightBooking;
